import React, { useEffect } from "react";
import {
    ActivityLogDropdownValue,
    ActivityLogEntry,
    FeatureRequest,
} from "interfaces/feature-request";
import moment from "moment-timezone";
import { get, isArray, isObject, isString } from "lodash";
import { TabHeader } from "../shared/generic";
import Table, { TableColumn } from "../shared/Table";
import { Translate, useTranslate } from "hooks/common/useTranslate/useTranslate";
import useFetch from "hooks/common/useFetch/useFetch";
import LoadingIndicator from "components/common/ui/LoadingIndicator/LoadingIndicator";
import LoadingWrapper from "../shared/LoadingWrapper";

const getValueField = (columnType: string): string => {
    switch (columnType) {
        case "color":
            return "label.text";
        case "date":
            return "date";
        case "dropdown":
            return "chosenValues";
        case "long-text":
            return "text";
        default:
            return "value";
    }
};

const isDropdownValue = (value: unknown): value is ActivityLogDropdownValue[] =>
    isArray(value) &&
    value.every((item) => isObject(item) && "name" in item && isString(item.name));

const extractValues = (
    data: ActivityLogEntry["data"],
    translate: Translate
): { oldValue: string; newValue: string } | undefined => {
    const valueField = getValueField(data.columnType);
    const oldValue = get(data.previousValue, valueField);
    const newValue = get(data.value, valueField);

    if (data.columnType === "dropdown" && isDropdownValue(oldValue) && isDropdownValue(newValue)) {
        return {
            oldValue: oldValue.map((v: ActivityLogDropdownValue) => v.name).join(", "),
            newValue: newValue.map((v: ActivityLogDropdownValue) => v.name).join(", "),
        };
    } else if (typeof newValue === "string" && typeof oldValue === "string") {
        return {
            oldValue: oldValue.trim(),
            newValue: newValue.trim(),
        };
    } else if (typeof newValue === "number" && typeof oldValue === "number") {
        return {
            oldValue: String(oldValue),
            newValue: String(newValue),
        };
    } else if (data.columnType === "doc") {
        return {
            oldValue: translate("featureRequests.featureRequestUpdates.documentUpdated"),
            newValue: translate("featureRequests.featureRequestUpdates.documentUpdated"),
        };
    }

    throw new Error("Unknown value type");
};

interface ActivityRow {
    id: string | number;
    createdAt: number;
    field: string;
    newValue: string;
}

const UpdatesTab: React.FC<{
    featureRequest: FeatureRequest;
}> = ({ featureRequest: { id } }) => {
    const translate = useTranslate();

    const { data, loading, fetchData } = useFetch<ActivityLogEntry[]>();

    useEffect(() => {
        fetchData(`/api/feature-requests/${id}/activityLogs`);
    }, [id, fetchData]);

    const activityLogs = data || [];

    const rows = activityLogs.reduce<ActivityRow[]>((acc, log) => {
        const extracted = extractValues(log.data, translate);

        return extracted
            ? [
                  ...acc,
                  {
                      id: log.id,
                      createdAt: log.createdAt,
                      field: log.data.columnTitle,
                      ...extracted,
                  },
              ]
            : acc;
    }, []);

    const columns: TableColumn<ActivityRow>[] = [
        {
            key: "createdAt",
            header: translate("featureRequests.featureRequestUpdates.updated"),
            sortable: true,
            render: (row) => moment.unix(row.createdAt).tz("Europe/Helsinki").format("DD.MM.YYYY"),
        },
        {
            key: "field",
            header: translate("featureRequests.featureRequestUpdates.field"),
            sortable: true,
        },
        {
            key: "newValue",
            header: translate("featureRequests.featureRequestUpdates.newContent"),
            render: (row) => <span>{row.newValue}</span>,
        },
    ];

    return (
        <>
            <TabHeader>
                {translate("featureRequests.featureRequestUpdates.updateHistory")}
            </TabHeader>
            {loading ? (
                <LoadingWrapper>
                    <LoadingIndicator isLoading={loading} />
                </LoadingWrapper>
            ) : (
                <Table data={rows} columns={columns} initialOrderBy="createdAt" />
            )}
        </>
    );
};

export default UpdatesTab;
